import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Code, Sparkle } from '@lifeomic/chromicons';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import urls from '../../../../../util/urls';

import { FileSystemComponent } from '../../../../AITemplates';
import { TemplateSummary } from '../../../../AITemplates/models';
import { useTemplatesContext } from './Context';
import { GenerateModal } from './GenerateModal';

const messages = defineMessages({
  generateReport: {
    id: 'templates.generateReport',
    defaultMessage: 'Generate Report',
  },
  generatingReport: {
    id: 'templates.generatingReport',
    defaultMessage: 'Generating Report',
  },
  manageTemplate: {
    id: 'templates.manageTemplate',
    defaultMessage: 'Manage Template',
  },
});

const useStyles = makeStyles(theme => ({
  bkgd: {
    backgroundColor: theme.palette.common.white,
  },
  dotLoader: {
    fill: `${theme.palette.common.white} !important`,
  },
  actionButton: {
    minWidth: theme.pxToRem(18),
    '&:not(:last-child)': {
      // table module actions has a :only-of-type selector that is very specific
      // and causes the button to have no margins. It's only a bug when there is a single button and a single link.
      marginRight: `${theme.spacing(2)} !important`,
    },
  },
}));

export const TemplatesContent: React.FC = () => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const { invocations } = useTemplatesContext();

  const [triggeringGeneration, setTriggeringGeneration] = useState<
    TemplateSummary | undefined
  >(undefined);

  return (
    <Box direction="column" className={classes.bkgd} margin={1}>
      <GenerateModal
        isOpen={!!triggeringGeneration}
        onDismiss={() => setTriggeringGeneration(undefined)}
        templateId={
          triggeringGeneration?.durableId || triggeringGeneration?.id || ''
        }
        activeImpression={undefined}
      />
      <FileSystemComponent
        folderNodeActions={() => <></>}
        templateNodeActions={(template: TemplateSummary) => {
          const isGenerating = invocations.some(
            invocation =>
              (invocation.status === 'pending' ||
                invocation.status === 'processing') &&
              (invocation.agentInput.template_id === template.id ||
                invocation.agentInput.template_id === template.durableId),
          );
          const title = isGenerating
            ? formatMessage(messages.generatingReport)
            : formatMessage(messages.generateReport);
          return (
            <>
              <Tooltip title={title}>
                {isGenerating ? (
                  <DotLoader
                    size={0}
                    dotStyle={classes.dotLoader}
                    className={classes.actionButton}
                  />
                ) : (
                  <IconButton
                    disabled={isGenerating}
                    className={classes.actionButton}
                    aria-label={formatMessage(messages.generateReport)}
                    color="inverse"
                    icon={Sparkle}
                    onClick={() => {
                      setTriggeringGeneration(template);
                    }}
                  />
                )}
              </Tooltip>
              <Tooltip title={formatMessage(messages.manageTemplate)}>
                <IconButtonLink
                  className={classes.actionButton}
                  aria-label={formatMessage(messages.manageTemplate)}
                  to={urls.app.workflows.reportTemplates(template.id)}
                  icon={Code}
                  color="inverse"
                />
              </Tooltip>
            </>
          );
        }}
      />
    </Box>
  );
};
